import { useRef } from 'react';
import { objectShallowEquals } from 'utils/object/objectShallowEquals';

export interface SmartDeps<TDeps> {
  /**
   * Previous version of deps or null on first render
   */
  prev: TDeps | null;
  /**
   * To be used as a dependency of some hook
   */
  version: number;
}

/**
 * Provide better control on deps - custom comparation and checking what changed
 *
 * IMPORTANT: use this hook only when reasonable. It is not meant to replace React's
 * standard deps mechanism. If you don't need it, keep your code simple and use standard deps.
 *
 * There are many cases when you need a better control on deps - either see what exactly
 * changed in deps or provide more complex than just shallow array comparison.
 * This can be done by some ad-hoc tricks, but it may lead to poorly readable
 * and/or error prone code.
 * This hook is designed to provide a standard, reasonably elegant way for solving
 * such cases.
 *
 * @example
 * const fooBarDeps = useTraceableDeps({
 *   foo: props.foo,
 *   bar: props.bar,
 * });
 *
 * useEffect(
 *   () => {
 *     if (fooBarDeps.prev === null) {
 *       console.log('first render');
 *     } else if (fooBarDeps.prev.foo !== props.foo) {
 *       console.log('foo changed to', fooBarDeps.prev.foo);
 *     }
 *     reloadSomething({refreshFooToo: fooBarDeps.changed.foo});
 *   }
 *   [fooBarDeps.version],
 * );
 */
export const useTraceableDeps = <TDeps,>(
  deps: TDeps,
  isEqual: (current: TDeps, prev: TDeps) => boolean = objectShallowEquals,
): SmartDeps<TDeps> => {
  const versionRef = useRef(0);
  const prevRef = useRef<TDeps | null>(null);

  if (prevRef.current !== null && !isEqual(deps, prevRef.current)) {
    versionRef.current += 1;
  }

  const result = {
    prev: prevRef.current,
    version: versionRef.current,
  };

  prevRef.current = deps;
  return result;
};
